// The fleet as the admin sees it (docs/tasks/35): cabinet types, the cabinets
// of each, and each cabinet's health. Built from the endpoints that exist today
// (one call per cabinet); a summary endpoint replaces the fan-out later (B4).

export const HARDWARE_LABEL = {
  "jetson-orin-nano": "Jetson Orin Nano",
  "raspberry-pi-5": "Raspberry Pi 5",
  "raspberry-pi-4": "Raspberry Pi 4",
  emulator: "Cloud emulator",
};

export const STATUS_STYLE = {
  service: "bg-rose-600 text-white",
  online: "bg-emerald-100 text-emerald-800",
  degraded: "bg-amber-100 text-amber-800",
  unknown: "bg-slate-100 text-slate-700",
  offline: "bg-rose-100 text-rose-800",
  revoked: "bg-slate-200 text-slate-500",
};

// What health's issue and warning codes mean (handbook ch. 13 and 16).
export const ISSUE_TEXT = {
  offline: "offline",
  revoked: "revoked",
  no_report: "has never reported its condition",
  recognition_disabled: "recognition is off",
  model_error: "the recognition model failed to load",
  camera_down: "a camera is down",
  report_stale: "its condition report is stale",
  cpu_hot: "CPU hot",
  disk_low: "disk almost full",
  door_on_camera: "door read from the camera (no contact sensor)",
  emulated: "a cloud emulator, no hardware",
};

export function isEmulator(cab) {
  return (cab?.device?.hardware || cab?.hardware || "") === "emulator";
}

export function deviceIdOf(d) {
  return d?.id || d?.device_id;
}

export function ago(ts) {
  if (!ts) return "never";
  const t = typeof ts === "number" ? ts : Date.parse(ts) / 1000;
  const s = Math.max(0, Date.now() / 1000 - t);
  if (s < 90) return `${Math.round(s)} s ago`;
  if (s < 5400) return `${Math.round(s / 60)} min ago`;
  if (s < 172800) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} days ago`;
}

// Labels for camera ids from the type's mounts: "camera 0 — top".
export function cameraLabels(ctype) {
  const out = {};
  for (const c of ctype?.cameras || []) out[c.id] = `camera ${c.id}${c.mount ? ` — ${c.mount}` : ""}`;
  return out;
}

async function settle(promise) {
  try {
    return { ok: true, value: await promise };
  } catch (e) {
    return { ok: false, error: e };
  }
}

// Everything the fleet and type pages need, in one go. Calls a role may not
// make (e.g. a product manager listing cabinets) come back as errors, not throws.
// onPartial, when given, receives the fleet as soon as the lists are in (each
// cabinet's health still null), so a page can draw before every cabinet answers:
// after a quiet spell the first calls wait for the Function App to wake.
export async function loadFleet(api, onPartial) {
  const [typesR, devicesR, releasesR] = await Promise.all([
    settle(api.listCabinetTypes()),
    settle(api.listDevices()),
    settle(api.listCodeReleases()),
  ]);
  const types = typesR.ok ? typesR.value : [];
  const devices = devicesR.ok ? (Array.isArray(devicesR.value) ? devicesR.value : devicesR.value?.data || []) : [];
  const releases = releasesR.ok ? releasesR.value : [];
  const newestRuntime = releases
    .filter((r) => (r.kind || "runtime") === "runtime" && (r.status || "ready") === "ready")
    .map((r) => r.version)
    .sort()
    .pop() || null;

  const errors = { types: typesR.ok ? null : typesR.error, devices: devicesR.ok ? null : devicesR.error };
  onPartial?.({ types, cabinets: devices.map((d) => ({ device: d, id: deviceIdOf(d), health: null, type: null, temperature: null, loading: true })),
    newestRuntime, errors });
  const cabinets = await Promise.all(devices.map(async (d) => {
    const id = deviceIdOf(d);
    const [h, t, temp] = await Promise.all([settle(api.getDeviceHealth(id)), settle(api.getDeviceCabinetType(id)),
      settle(api.getDeviceCabinetTemperature(id))]);
    return {
      device: d,
      id,
      health: h.ok ? h.value : null,
      type: t.ok ? t.value?.cabinet_type || null : null,
      temperature: temp.ok ? temp.value : null,
    };
  }));
  return {
    types,
    cabinets,
    newestRuntime,
    errors: {
      types: typesR.ok ? null : typesR.error,
      devices: devicesR.ok ? null : devicesR.error,
    },
  };
}

export function codeVersionOf(cab) {
  return cab.health?.report?.system?.code_version || null;
}

// The alerts the fleet page lists first: what needs a person.
export function alertsFor(cab, newestRuntime) {
  const out = [];
  const h = cab.health;
  const revoked = cab.device?.revoked || h?.status === "revoked";
  // An emulator is a test tool, not a cabinet that can need a person.
  if (revoked || isEmulator(cab)) return out;
  // In service (22 Oct, dad8ac4): the cloud refuses purchases until the cabinet is whole again.
  if (h?.service?.active) out.push({ level: "bad", text: `in service since ${ago(h.service.since)}: purchases refused${h.service.reason ? ` (${h.service.reason})` : ""}` });
  // A weight-only cabinet has no cameras and no model by design: recognition off and
  // "no model artifact" are not faults there (weight session, 22 Oct).
  const camerasExpected = h?.report?.cameras?.expected;
  const weightOnly = camerasExpected === 0;
  const RECOGNITION_ONLY = ["model_error", "recognition_disabled"];
  for (const code of h?.issues || []) {
    if (weightOnly && RECOGNITION_ONLY.includes(code)) continue;
    out.push({ level: "bad", text: ISSUE_TEXT[code] || code });
  }
  // A camera down is not always an issue in health (22 Oct: the Jetson ran on one
  // camera all afternoon, healthy below expected, issues empty), so read the report.
  const cams = h?.report?.cameras;
  if (h?.online && cams && cams.expected > 0 && cams.healthy < cams.expected && !(h.issues || []).includes("camera_down")) {
    out.push({ level: "bad", text: `${cams.healthy} of ${cams.expected} cameras working${cams.error ? ` (${cams.error})` : ""}` });
  }
  for (const code of h?.warnings || []) out.push({ level: "warn", text: ISSUE_TEXT[code] || code });
  if (h?.report?.model_error && !weightOnly && !(h.issues || []).includes("model_error")) {
    out.push({ level: "warn", text: `model: ${h.report.model_error}` });
  }
  if (!cab.type) out.push({ level: "warn", text: "no cabinet type: it cannot be trained or get a type-scoped gallery" });
  const code = codeVersionOf(cab);
  if (code && newestRuntime && code < newestRuntime && h?.online) {
    out.push({ level: "info", text: `runs ${code}, newest release is ${newestRuntime}` });
  }
  for (const d of driftOf(cab, cab.type)) {
    out.push({ level: "warn", text: `${d.what}: runs ${d.actual}, its type expects ${d.expected}` });
  }
  return out;
}

// ---------- What a cabinet runs against what its type expects (docs/tasks/35, phase 3) ----------

export const PLATFORM_LABEL = {
  jetson_orin: "Jetson Orin",
  raspberry_pi_5: "Raspberry Pi 5 (+ Hailo-8)",
  raspberry_pi_4: "Raspberry Pi 4",
};

// The bundle profile a cabinet's board is, for picking the detector engine its type expects.
export function platformOf(cab) {
  return { "jetson-orin-nano": "jetson_orin", "raspberry-pi-5": "raspberry_pi_5", "raspberry-pi-4": "raspberry_pi_4" }[
    cab?.device?.hardware || cab?.health?.hardware
  ] || null;
}

export function modelsOf(health) {
  const out = {};
  for (const m of health?.report?.models || []) out[m.role] = m;
  return out;
}

// trt_plan (what the device reports) satisfies an expected trt_fp16 / trt_fp32: the plan's precision is fixed when it is built.
function engineFamily(e) {
  if (!e) return null;
  if (e.startsWith("trt")) return "trt";
  if (e.startsWith("onnx")) return "onnx";
  return e;
}

function shaMatches(expected, actual) {
  if (!expected || !actual) return null;
  const n = Math.min(expected.length, actual.length);
  return expected.slice(0, n).toLowerCase() === actual.slice(0, n).toLowerCase();
}

// Every difference between a cabinet's report and its type's expected stack.
// An empty list when the type expects nothing, or the cabinet reports no models yet.
export function driftOf(cab, type) {
  const exp = type?.expected || {};
  const h = cab?.health;
  const models = modelsOf(h);
  const out = [];
  if (!h?.report?.models) return Object.keys(exp).length ? [{ what: "models", expected: "a report", actual: "the cabinet's runtime does not report its models yet" }] : [];
  if (exp.encoder_version && models.encoder?.version && models.encoder.version !== exp.encoder_version) {
    out.push({ what: "encoder", expected: exp.encoder_version, actual: models.encoder.version });
  }
  const d = exp.detector;
  if (d) {
    const got = models.detector;
    if (!got) out.push({ what: "detector", expected: d.name || "a detector", actual: "none" });
    else {
      if (got.loaded === false) out.push({ what: "detector", expected: "loaded", actual: "named but not loaded" });
      if (d.name && got.name && !got.name.startsWith(d.name)) out.push({ what: "detector", expected: d.name, actual: got.name });
      const eng = (d.engines || {})[platformOf(cab)];
      if (eng) {
        if (engineFamily(eng.kind) !== engineFamily(got.engine)) out.push({ what: "detector engine", expected: eng.kind, actual: got.engine || "unknown" });
        const want = eng.sha256 || eng.hef_sha256;
        if (shaMatches(want, got.file_sha256) === false) out.push({ what: "detector file", expected: want.slice(0, 12), actual: got.file_sha256 });
      }
    }
  }
  if (exp.review_window_mode && h.report.review_window_mode && h.report.review_window_mode !== exp.review_window_mode) {
    out.push({ what: "window mode", expected: exp.review_window_mode, actual: h.report.review_window_mode });
  }
  return out;
}
